// src/pages/register-options/RegisterTableCredito.jsx
import React, {
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useEffect,
} from "react";
import axios from "axios";
import Select from "react-select";
import moment from "moment-timezone";
import "moment/locale/es";
import PreloadFetch from "../PreloadFetch";
import { ToastContainer, toast } from "react-toastify";

import {
    handleTextInput,
    handleEmailInput,
    handleNumberInput,
    handlePriceInput,
} from "../../utils/inputUtils";

moment.locale("es");

/* ============ CONFIG API (copiada de Contact.jsx) ============ */
const DNI_API_URL = "https://apiperu.dev/api/dni";
const DNI_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;

/* ============ API interna ============ */
const API_BASE = process.env.REACT_APP_API || "";
const CREDIT_BY_DNI_URL =
    process.env.REACT_APP_REGISTER_CREDIT_BY_DNI ||
    `${API_BASE}/register/credit-by-dni`;

/* ============ AXIOS INSTANCES ============ */
// Instancia aislada para APIPERU (sin interceptores globales)
const axiosDni = axios.create({
    baseURL: DNI_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${DNI_API_BEARER || ""}`,
    },
    withCredentials: false,
    validateStatus: () => true,
});

// Instancia para API interna
const axiosInternal = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

import { pickActiveCredit } from "../../utils/HerlperRegisters"


const RegisterTableCredito = forwardRef((props, ref) => {
    const { options: phoneOptions, onSendDataCat, onSendDataSegment } = props;

    const [selectedPhone, setSelectedPhone] = useState(null);
    const [lastDniQueried, setLastDniQueried] = useState("");
    const [loading, setLoading] = useState(false);

    // Validación de precio
    const [priceError, setPriceError] = useState("");

    // Toggle: automatización ON/OFF (empieza activado)
    const [autoNames, setAutoNames] = useState(true);

    // Abort + debounce
    const abortDniRef = useRef(null);
    const abortCreditRef = useRef(null);
    const debounceRef = useRef(null);

    const createDateSet = moment
        .tz("America/Lima")
        .format("dddd DD [de] MMMM [del] YYYY");

    /* ====== Refs de inputs ====== */
    const catRef = useRef();
    const subCatRef = useRef();
    const segmentRef = useRef();

    const phonePriceRef = useRef();
    const nameRef = useRef();
    const paternalSurRef = useRef();
    const maternalSurRef = useRef();

    const fmRef = useRef();
    const fmAmountRef = useRef();
    const imeiRef = useRef();
    const dniRef = useRef();
    const emailRef = useRef();
    const phoneNumberRef = useRef();

    const cashAmountRef = useRef();
    const onlinePayRef = useRef();
    const onlineAmountRef = useRef();

    /* ====== Helpers de precio ====== */
    const parseMoney = (v) => {
        if (v == null) return 0;
        const s = String(v).replace(/[^\d.,]/g, "").replace(/,/g, "");
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
    };

    const validatePrice = () => {
        const n = parseMoney(phonePriceRef.current?.value);
        if (n <= 0) {
            setPriceError("Ingrese un precio mayor a 0.");
            phonePriceRef.current?.focus();
            return false;
        }
        setPriceError("");
        return true;
    };

    const onPriceBlur = () => {
        const n = parseMoney(phonePriceRef.current?.value);
        if (n <= 0) {
            setPriceError("Ingrese un precio mayor a 0.");
        } else {
            setPriceError("");
            phonePriceRef.current.value = n.toFixed(2);
        }
    };

    useImperativeHandle(ref, () => ({
        getValues: () => ({
            cat: catRef.current?.value,
            subCat: subCatRef.current?.value,
            segment: segmentRef.current?.value,
            phone: selectedPhone?.value || null,
            phonePrice: phonePriceRef.current?.value,
            name: nameRef.current?.value,
            paternalSur: paternalSurRef.current?.value,
            maternalSur: maternalSurRef.current?.value,
            fm: fmRef.current?.value,
            fmAmount: fmAmountRef.current?.value,
            imei: imeiRef.current?.value,
            dni: dniRef.current?.value,
            email: emailRef.current?.value,
            phoneNumber: phoneNumberRef.current?.value,
            cashAmount: cashAmountRef.current?.value,
            onlinePay: onlinePayRef.current?.value,
            onlineAmount: onlineAmountRef.current?.value,
            __isPriceValid: validatePrice(),
        }),
        validateRow: () => validatePrice(),
    }));

    const handleChangePhone = (option) => setSelectedPhone(option);

    const clearPending = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        try {
            abortDniRef.current?.abort?.();
        } catch {}
        try {
            abortCreditRef.current?.abort?.();
        } catch {}
    };

    const clearRowValues = ({ keepDni = true } = {}) => {
        setSelectedPhone(null);
        setPriceError("");
        if (phonePriceRef.current) phonePriceRef.current.value = "0.00";
        if (nameRef.current) nameRef.current.value = "";
        if (paternalSurRef.current) paternalSurRef.current.value = "";
        if (maternalSurRef.current) maternalSurRef.current.value = "";
        if (imeiRef.current) imeiRef.current.value = "";
        if (!keepDni && dniRef.current) dniRef.current.value = "";
        if (emailRef.current) emailRef.current.value = "";
        if (phoneNumberRef.current) phoneNumberRef.current.value = "";
        if (fmRef.current) fmRef.current.value = "0";
        if (fmAmountRef.current) fmAmountRef.current.value = "0.00";
        if (cashAmountRef.current) cashAmountRef.current.value = "0.00";
        if (onlinePayRef.current) onlinePayRef.current.value = "0";
        if (onlineAmountRef.current) onlineAmountRef.current.value = "0.00";
        setLastDniQueried("");
    };

    const changeCat = (e) => {
        onSendDataCat?.({ cat: e.target.value });
    };

    const changeSegment = (e) => {
        const newSeg = e.target.value;

        // 1) Avisar al padre
        onSendDataSegment?.({
            cat: catRef.current?.value,
            subCat: subCatRef.current?.value,
            segment: newSeg,
        });

        // 2) Limpiar y cancelar
        clearPending();
        clearRowValues({ keepDni: true });

        // 3) Si ya hay DNI válido y automatización ON, re-ejecuta sin debounce
        const v = (dniRef.current?.value || "").trim();
        if (autoNames && v.length === 8) autoFillByDni(v, newSeg, false);
    };

    /* ==================== Helpers ==================== */
    const toTitleCase = (s = "") =>
        s
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b([a-záéíóúñü])([a-záéíóúñü]*)/gi, (_, a, b) => a.toUpperCase() + b);

    const setNamesFromApi = (payload) => {
        const root = payload?.data ? payload.data : payload || {};
        const nombres = toTitleCase(root.nombres || root.nombre || "");
        const apPat = toTitleCase(root.apellido_paterno || root.apellidoPaterno || "");
        const apMat = toTitleCase(root.apellido_materno || root.apellidoMaterno || "");

        if (nameRef.current) nameRef.current.value = nombres;
        if (paternalSurRef.current) paternalSurRef.current.value = apPat;
        if (maternalSurRef.current) maternalSurRef.current.value = apMat;
    };

    const matchPhoneOptionByLabel = (phoneLabel = "") => {
        if (!phoneOptions || !Array.isArray(phoneOptions)) return null;
        const norm = (s) => String(s || "").toLowerCase().trim();
        const target = norm(phoneLabel);
        return (
            phoneOptions.find((opt) => norm(opt.label) === target) ||
            phoneOptions.find((opt) => norm(opt.label).includes(target)) ||
            null
        );
    };

    const fillFromCreditRow = (row = {}) => {
        const opt = matchPhoneOptionByLabel(row.fr_phone_name);
        setSelectedPhone(opt || null);

        if (phonePriceRef.current) {
            const v = row.fr_phone_price != null ? Number(row.fr_phone_price) : 0;
            phonePriceRef.current.value = Number.isFinite(v) ? v.toFixed(2) : "0.00";
            if (v <= 0) setPriceError("Ingrese un precio mayor a 0.");
            else setPriceError("");
        }

        if (imeiRef.current) imeiRef.current.value = row.fr_imei || "";
        if (dniRef.current) dniRef.current.value = row.fr_dni || dniRef.current.value || "";
        if (emailRef.current) emailRef.current.value = row.fr_email || "";
        if (phoneNumberRef.current) phoneNumberRef.current.value = row.fr_phone_number || "";
    };

    /* ==================== Fetchers (axios) ==================== */
    const fetchDniApi = async (dniVal) => {
        if (!/^\d{8}$/.test(dniVal)) return null;
        if (!DNI_API_BEARER) return null;

        try {
            try {
                abortDniRef.current?.abort?.();
            } catch {}
            abortDniRef.current = new AbortController();

            const res = await axiosDni.post("", { dni: dniVal }, { signal: abortDniRef.current.signal });
            if (res.status === 200 && res.data) return res.data;
            return null;
        } catch (err) {
            console.error("[DNI API] Error controlado:", err?.message || err);
            return null;
        }
    };

    const fetchCreditByDni = async (dniVal) => {
        try {
            try {
                abortCreditRef.current?.abort?.();
            } catch {}
            abortCreditRef.current = new AbortController();

            const auth = localStorage.getItem("token") || "";
            const res = await axiosInternal.post(
                CREDIT_BY_DNI_URL.replace(API_BASE, ""),
                { dni: dniVal },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: auth ? `Bearer ${auth}` : "",
                    },
                    signal: abortCreditRef.current.signal,
                }
            );
            return res?.data?.data || res?.data?.credit || res?.data || null;
        } catch (err) {
            console.error("[CREDIT BY DNI] Error:", err);
            return null;
        }
    };

    /* ==================== Toggle handler ==================== */
    const onToggleAuto = (checked) => {
        setAutoNames(checked);

        if (!checked) {
            clearPending();
            return;
        }

        const v = (dniRef.current?.value || "").trim();
        if (/^\d{8}$/.test(v)) {
            const segVal = segmentRef.current?.value || "1";
            autoFillByDni(v, segVal, false);
        }
    };

    /* ==================== Flujo principal ==================== */
    const autoFillByDni = async (dniVal, segVal, useDebounce = true) => {
        if (!autoNames) return;
        if (catRef.current?.value !== "1" || subCatRef.current?.value !== "1") return;

        if (useDebounce) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => doAutoFill(dniVal, segVal), 2000);
        } else {
            doAutoFill(dniVal, segVal);
        }
    };

    const doAutoFill = async (dniVal, segVal) => {
        if (!autoNames) return;
        const key = `${dniVal}-${segVal}`;
        if (lastDniQueried === key) return;
        setLastDniQueried(key);

        setLoading(true);
        try {
            if (segVal === "2") {
                // 1) Buscar crédito VIGENTE en MovilesPay
                const payload = await fetchCreditByDni(dniVal);
                const row = pickActiveCredit(payload);

                if (!row) {
                    // No hay crédito vigente (activo + enrolado + no finalizado) => NO autocompletar
                    clearRowValues({ keepDni: true });
                    // Opcional: mensaje suave en consola
                    console.info("Sin crédito vigente para el DNI (MovilesPay).");
                    toast.warn("Sin crédito vigente para el DNI.", {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                    });
                    return;
                }

                // 2) Completar columnas desde fp_credit
                fillFromCreditRow(row);

                // 3) Nombres desde API externa (opcional, como lo tenías)
                const dniInfo = await fetchDniApi(dniVal);
                if (dniInfo) setNamesFromApi(dniInfo);
            } else {
                // Payjoy (1), Efectiva (3) y CrediSmart (4): solo nombres desde API externa
                const dniInfo = await fetchDniApi(dniVal);
                if (dniInfo) setNamesFromApi(dniInfo);
            }
        } finally {
            setLoading(false);
        }
    };

    // Typing en DNI: espera 2s al llegar a 8 dígitos antes de buscar
    const onDniChange = (e) => {
        const v = (e.target.value || "").trim();
        handleNumberInput(e, 8);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        if (v.length === 8 && autoNames) {
            const segVal = segmentRef.current?.value || "1";
            autoFillByDni(v, segVal, true);
        }
    };

    // Limpia abort controllers al desmontar
    useEffect(() => {
        return () => {
            try {
                abortDniRef.current?.abort?.();
            } catch {}
            try {
                abortCreditRef.current?.abort?.();
            } catch {}
        };
    }, []);

    return (
        <div className="registerTable registerTable1 registerTableInList">
            {loading && <PreloadFetch className="mainPreloadOpacity" />}
            <table>
                <caption>
                    <div>
                        <span>{createDateSet}</span>
                        <label className="toggleTwo" style={{ marginLeft: 12 }}>
                            <input
                                type="checkbox"
                                className="toggleTwo-input"
                                checked={autoNames}
                                onChange={(e) => onToggleAuto(e.target.checked)}
                            />
                            <span className="toggleTwo-slider" />
                            <span className="toggleTwo-text">Autocompletar por DNI</span>
                        </label>
                    </div>
                </caption>

                <thead>
                    <tr>
                        <th>Tipo de Registro</th>
                        <th>Tipo de Producto</th>
                        <th>Fuente</th>
                        <th>Equipo Celular</th>
                        <th>Precio</th>
                        <th>Nombre</th>
                        <th>Apellido 1</th>
                        <th>Apellido 2</th>
                        <th>FM</th>
                        <th>Monto FM</th>
                        <th>IMEI</th>
                        <th>DNI</th>
                        <th>EMAIL</th>
                        <th>Celular</th>
                        <th>Monto Efectivo</th>
                        <th>Pago Online</th>
                        <th>Monto Online</th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td className="cat">
                            <select defaultValue="1" onChange={onSendDataCat ? (e) => onSendDataCat({ cat: e.target.value }) : undefined} ref={catRef}>
                                <option value="1">Crédito</option>
                                <option value="2">Contado</option>
                                <option value="3">Egreso</option>
                                <option value="4">Ingreso</option>
                                <option value="5">Retiro</option>
                            </select>
                        </td>
                        <td className="subCat">
                            <select defaultValue="1" ref={subCatRef} disabled>
                                <option value="1">Télefono</option>
                            </select>
                        </td>
                        <td className="segment">
                            <select defaultValue="1" onChange={changeSegment} ref={segmentRef}>
                                <option value="1">Payjoy</option>
                                <option value="2">MovilesPay</option>
                                <option value="3">Efectiva</option>
                                <option value="4">CrediSmart</option>
                            </select>
                        </td>
                        <td width="12%" className="tableWidth">
                            <Select
                                options={phoneOptions}
                                value={selectedPhone}
                                onChange={handleChangePhone}
                                placeholder={"SELECCIÓNE UN EQUIPO"}
                                className="selectForm formItem selectPhone"
                                name="phoneName"
                            />
                        </td>

                        {/* PRECIO + validación */}
                        <td className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                onBlur={onPriceBlur}
                                ref={phonePriceRef}
                                inputMode="decimal"
                                aria-invalid={!!priceError}
                                title={priceError || "Precio del equipo"}
                                className={priceError ? "input-error" : ""}
                            />
                        </td>

                        {/* Nombre / Apellidos (bloqueados cuando el toggle está ON) */}
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={nameRef}
                                id="nameAdd"
                                disabled={autoNames}
                                aria-disabled={autoNames}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={paternalSurRef}
                                id="paternalSurAdd"
                                disabled={autoNames}
                                aria-disabled={autoNames}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={maternalSurRef}
                                id="maternalSurAdd"
                                disabled={autoNames}
                                aria-disabled={autoNames}
                            />
                        </td>

                        <td width="3%">
                            <select defaultValue="0" ref={fmRef}>
                                <option value="0">No</option>
                                <option value="1">Sí</option>
                            </select>
                        </td>
                        <td className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={fmAmountRef}
                            />
                        </td>
                        <td width="6.5%">
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 15)}
                                ref={imeiRef}
                                id="imeiAdd"
                            />
                        </td>
                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 8)}
                                onChange={onDniChange}
                                ref={dniRef}
                                id="dniAdd"
                            />
                        </td>
                        <td width="10%">
                            <input
                                type="text"
                                onInput={(e) => handleEmailInput(e, 50)}
                                ref={emailRef}
                                id="emailAdd"
                            />
                        </td>
                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 9)}
                                ref={phoneNumberRef}
                                id="phoneNumberAdd"
                            />
                        </td>
                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={cashAmountRef}
                            />
                        </td>
                        <td>
                            <select defaultValue="0" ref={onlinePayRef} id="onlinePayAdd">
                                <option value="0">Selecciónar</option>
                                <option value="1">Yape</option>
                                <option value="5">Culqi Yape</option>
                                <option value="2">Tarjeta</option>
                                <option value="6">Culqi Tarjeta</option>
                                <option value="3">Transferencia</option>
                            </select>
                        </td>
                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={onlineAmountRef}
                                id="onlineAmountAdd"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

export default RegisterTableCredito;
